import React, { useImperativeHandle, useState } from 'react';
import { Code } from 'lucide-react';
import { EditorContent, useEditor } from '@tiptap/react';
import { useContextEffect } from '../../../../hooks';
import MenuBar from './menu-toolbar';
import useExtensions from './use-extensions';
import 'katex/dist/katex.min.css';

const TiptapCodeEditor = ({
  onUpdate,
  value,
  textPlaceholder = 'You can start typing here',
  forwardRef,
  autofocus = false,
  onlyCode = false,
  disabled = false,
  editorVariant
}) => {
  const [valueSet, setValueSet] = useState(false);
  const [lineCount, setLineCount] = useState(1);
  const variant = editorVariant || (onlyCode ? 'code' : 'default');
  const extensions = useExtensions({ onlyCode, textPlaceholder });

  const editor = useEditor({
    extensions,
    onUpdate: ({ editor: ed }) => {
      const newContent = ed.getHTML();
      onUpdate?.(newContent);
      if (variant === 'code') {
        const text = ed.getText();
        setLineCount(Math.max(1, text.split('\n').length));
      }
    },
    autofocus,
    editable: !disabled,
    content: value,
    editorProps: {
      attributes: {
        class: disabled ? '' : 'cpp-editable'
      }
    }
  });

  useContextEffect({
    deps: [valueSet, value],
    ctx: { editor },
    cb: (ctx) => {
      if (valueSet) return;
      if (value) {
        ctx.editor.commands.setContent(value, true);
        setValueSet(true);
        if (variant === 'code') {
          setLineCount(Math.max(1, ctx.editor.getText().split('\n').length));
        }
      }
    }
  });

  useImperativeHandle(forwardRef, () => ({
    focus: () => {
      editor?.commands.focus();
    }
  }));

  const editorShell = (
    <div
      className={`tiptap-editor flex flex-col ${
        variant === 'subjective'
          ? 'tiptap-subjective-shell overflow-visible rounded-lg border border-border bg-white'
          : variant === 'code'
            ? 'tiptap-code-shell max-h-[min(50vh,28rem)] overflow-y-auto rounded-b-lg border border-t-0 border-gray-700 bg-[#1a1a1a]'
            : disabled
              ? 'tiptap-readonly-display w-full min-w-0'
              : 'rounded-[10px] bg-[#FAFAFA] px-4 pb-4'
      }`}
    >
      {variant === 'subjective' && !disabled && (
        <div className="border-b border-border bg-muted/40 px-2.5 py-2 sm:px-3">
          <MenuBar editor={editor} />
        </div>
      )}
      {variant !== 'code' && variant !== 'subjective' && !onlyCode && !disabled && (
        <MenuBar editor={editor} />
      )}
      <EditorContent
        editor={editor}
        className={`editor-container ${
          variant === 'subjective'
            ? 'min-h-40 px-3 py-2'
            : variant === 'code'
              ? 'min-h-48 px-3 py-2'
              : disabled
                ? 'readonly-question-html'
                : ''
        }`}
        spellCheck={false}
      />
    </div>
  );

  if (variant === 'code') {
    return (
      <div className="w-full space-y-2 md:space-y-3">
        <div className="flex items-center justify-between rounded-t-lg bg-gray-900 p-3 text-white">
          <div className="flex items-center gap-2">
            <Code className="h-4 w-4" />
            <span className="text-sm font-medium">Code Editor</span>
          </div>
          <div className="flex gap-2">
            <div className="h-3 w-3 rounded-full bg-red-500" />
            <div className="h-3 w-3 rounded-full bg-yellow-500" />
            <div className="h-3 w-3 rounded-full bg-green-500" />
          </div>
        </div>
        {editorShell}
        <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
          <p>Use proper indentation and include comments to explain your logic.</p>
          <p className="shrink-0">Lines: {lineCount}</p>
        </div>
      </div>
    );
  }

  if (variant === 'subjective') {
    return (
      <div className="space-y-2 md:space-y-3">
        {editorShell}
        <p className="text-xs text-muted-foreground">
          Tip: Use the toolbar to format your answer with headings, lists, and emphasis.
        </p>
      </div>
    );
  }

  // Read-only question stems: natural height, no flex-1 clipping on mobile.
  if (disabled) {
    return <div className="w-full min-w-0 max-w-full overflow-visible">{editorShell}</div>;
  }

  return (
    <div className="relative flex flex-1">
      <div className={`flex flex-1 flex-col ${onlyCode ? 'pure-code-block' : ''}`}>{editorShell}</div>
    </div>
  );
};

export default TiptapCodeEditor;
